import { useEffect, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminWaitlistEntry, AdminWaitlistResponse } from '@/types/admin'

export default function AdminWaitlistPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const [entries, setEntries] = useState<AdminWaitlistEntry[]>([])
  const [, setTotalEntries] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingId, setUpdatingId] = useState<number | null>(null)

  const fetchData = async () => {
    try {
      setError(null)
      setLoading(true)
      const [entriesRes] = await Promise.all([
        adminApi.waitlistEntries({ pageSize: 50 })
      ])
      const entriesData = (entriesRes as { data?: AdminWaitlistResponse | AdminWaitlistEntry[] }).data
      if (entriesData && 'items' in entriesData) {
        setEntries(entriesData.items)
        setTotalEntries(entriesData.total)
      } else if (Array.isArray(entriesData)) {
        setEntries(entriesData)
        setTotalEntries(entriesData.length)
      } else {
        setEntries([])
        setTotalEntries(0)
      }
    } catch (err) {
      setError((err as Error)?.message || 'Failed to load waitlist')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchData()
  }, [])

  const handleConvert = async (entry: AdminWaitlistEntry) => {
    if (updatingId) return
    setUpdatingId(entry.id)
    try {
      await adminApi.updateWaitlistStatus(entry.id, 'converted')
      await fetchData()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to convert user')
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8'>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Waitlist</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>Waitlist overview with conversions and statuses.</p>
            </div>
            <div className='hidden lg:flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-[16px]'>
                {initials}
              </div>
              <span className='text-[24px] font-bold text-[#353535]'>{displayName}</span>
            </div>
          </div>

          {/* Signups Table */}
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 sm:p-6'>
            <h3 className='text-[18px] font-bold text-[#151515] mb-4'>Recent Signups</h3>
            <div className='bg-[rgba(237,239,243,0.5)] rounded-[8px] overflow-hidden'>
              <div className='grid grid-cols-5 gap-2 px-4 py-2 text-start'>
                <span className='font-medium text-[14px] text-[#475467]'>Name</span>
                <span className='font-medium text-[14px] text-[#475467]'>Email</span>
                <span className='font-medium text-[14px] text-[#475467]'>Phone</span>
                <span className='font-medium text-[14px] text-[#475467]'>Status</span>
                <span className='font-medium text-[14px] text-[#475467] text-right'>Action</span>
              </div>
              {loading && <p className='text-[14px] text-[#4e4d4d] p-4'>Loading waitlist entries...</p>}
              {!loading && entries.length === 0 && <p className='text-[14px] text-[#4e4d4d] p-4'>No waitlist entries found.</p>}
              {!loading && entries.length > 0 && entries.map((entry) => (
                <div key={entry.id} className='grid grid-cols-5 gap-2 px-4 py-3 text-[14px] text-[#101828] border-b border-[#eaecf0] hover:bg-[#f9fafb] items-center text-start'>
                  <span className='font-medium'>{entry.firstName} {entry.lastName}</span>
                  <span className='text-[#4e4d4d]'>{entry.email}</span>
                  <span className='text-[#4e4d4d]'>{entry.phoneNumber || '—'}</span>
                  <span className={
                    entry.status === 'converted' ? 'text-[#15803d] font-bold capitalize' :
                    entry.status === 'contacted' ? 'text-blue-600 font-bold capitalize' :
                    'text-[#FF6600] font-bold capitalize'
                  }>
                    {entry.status}
                  </span>
                  <div className='flex justify-end gap-2 text-xs'>
                    {entry.status !== 'converted' && (
                      <button
                        className='px-3 py-1 rounded-[8px] bg-orange-50 text-[#FF6600] hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed'
                        onClick={() => handleConvert(entry)}
                        disabled={updatingId === entry.id}
                      >
                        {updatingId === entry.id ? 'Converting...' : 'Convert'}
                      </button>
                    )}
                    <button className='px-3 py-1 rounded-[8px] bg-[rgba(237,239,243,0.5)] text-[#151515] hover:bg-[rgba(237,239,243,0.8)]'>
                      Message
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className='text-[12px] text-[#4e4d4d] mt-3'>
              Showing recent waitlist signups with conversion actions.
            </p>
          </div>

          {error && <p className='text-[14px] text-red-600'>{error}</p>}
        </div>
      </div>
    </div>
  )
}
